import type { AuditLogItem } from '../api/auditLogs';

const actionLabels: Record<string, string> = {
  create_proxy: '创建节点',
  delete_proxy: '删除节点',
  patch_proxy: '修改节点',
  extend_proxy: '节点续费',
  reduce_proxy_time: '减少节点时间',
  batch_extend_proxy: '批量续费',
  transfer_proxy_time: '转移节点时间',
  restart_child: '重启节点进程',
  proxy_update_regions: '修改节点大区',
  create_node: '创建服务器',
  update_node: '修改服务器',
  delete_node: '删除服务器',
  control_node: '控制服务器',
  batch_control_node: '批量控制服务器',
  node_action: '服务器运维操作',
  rotate_join_token: '重置加入令牌',
  create_proxy_user: '创建用户',
  patch_proxy_user: '修改用户',
  delete_proxy_user: '删除用户',
  update_setting: '修改设置',
  update_notification_settings: '修改通知设置',
  update_node_credential: '修改服务器凭据',
  update_node_ops_profile: '修改服务器运维资料',
  proxy_died: '节点进程退出',
  proxy_respawn: '节点进程重建',
  proxy_expired_sweep: '节点到期扫描',
  node_offline_sweep: '服务器离线扫描',
  create_proxy_recovered: '节点创建恢复',
  create_proxy_giveup: '节点创建放弃',
  proxy_runtime_sync_pending: '节点配置待同步',
  proxy_delete_sync_pending: '节点删除待同步',
};

const systemActions = new Set([
  'proxy_died',
  'proxy_respawn',
  'proxy_expired_sweep',
  'node_offline_sweep',
  'create_proxy_recovered',
  'create_proxy_giveup',
  'proxy_runtime_sync_pending',
  'proxy_delete_sync_pending',
]);

const numericID = (payload: Record<string, unknown>, key: string): number | null => {
  const value = Number(payload[key] ?? 0);
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const auditActionLabel = (action: string): string => actionLabels[action] || action;

export const auditActor = (entry: AuditLogItem): string => {
  const explicit = entry.payload.by ?? entry.payload.requested_by ?? entry.payload.operator;
  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();
  return systemActions.has(entry.action) ? '系统任务' : '管理员';
};

const transferDuration = (secondsValue: unknown): string => {
  const seconds = Number(secondsValue ?? 0);
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return days > 0 ? `${days} 天 ${hours} 小时` : `${hours} 小时`;
};

export const describeAuditLog = (entry: AuditLogItem): string => {
  const payload = entry.payload;
  if (entry.action === 'transfer_proxy_time') {
    const sourceID = numericID(payload, 'source_proxy_id');
    const targetID = numericID(payload, 'target_proxy_id');
    const duration = transferDuration(payload.transferred_seconds);
    return `节点 #${sourceID ?? '?'} → #${targetID ?? '?'}${duration ? ` · ${duration}` : ''}`;
  }
  const proxyID = numericID(payload, 'proxy_id');
  if (proxyID) return `${auditActionLabel(entry.action)} #${proxyID}`;
  const userID = numericID(payload, 'proxy_user_id');
  if (userID && entry.action.includes('proxy_user')) return `${auditActionLabel(entry.action)} #${userID}`;
  const nodeID = numericID(payload, 'node_id') ?? numericID(payload, 'id');
  if (nodeID && (entry.action.includes('node') || entry.action === 'control_node')) {
    return `${auditActionLabel(entry.action)} #${nodeID}`;
  }
  return auditActionLabel(entry.action);
};

export const auditActionColor = (action: string): string => {
  if (action.startsWith('delete_') || action.includes('giveup')) return 'red';
  if (action.startsWith('create_') || action.includes('respawn') || action.includes('recovered')) return 'green';
  if (action.includes('pending') || action.includes('offline') || action.includes('expired')) return 'orange';
  if (action.includes('update') || action.includes('patch') || action.includes('extend') || action.includes('reduce') || action.includes('transfer')) return 'blue';
  return 'default';
};
